/**
 * useVqeVisualRunOnChange — "Run on change" for the Query Editor's Visual
 * view: after every committed visual edit, re-run the statement the view
 * shows so its results follow the query the way the Table Explorer does
 * (mirrors `useVqeRunOnChange`, the same setting's compact-popover twin —
 * see that file for the full safety rationale).
 *
 * Same mechanics, different target: the compact popover binds the run to
 * whatever statement sits under the (visible) editor's caret; the Visual
 * view has no caret — its target is the statement the view is anchored to,
 * resolved with `resolveVisualRun` (the exact function `runVisualStatement`
 * uses for a manual Execute), read from the live buffer at fire time.
 *
 * Guards, all required: the setting is on, the Visual view is showing (and
 * is this tab's active pane), and the resolved statement classifies as a
 * SELECT (`kind === 'select'`) — DML/DDL is never auto-run, whatever the
 * setting says. Commits are debounced (same `RUN_DEBOUNCE_MS` as the compact
 * popover) so a multi-step gesture runs once. As with the compact popover,
 * `select` is a RENDER classification, not a safety one, so the exact SQL is
 * re-checked at fire time against `autoRunSafety.ts`'s allowlist/write-token
 * scan before it is dispatched.
 */
import { useEffect, useRef } from 'react';

import type { DatabaseEngineId } from '../../../../core/engines';
import type { VisualStatementRun } from '../../visual-query-editor/execute/visualStatementRun';
import { useVqeStore } from '../../visual-query-editor/store/vqeStore';
import { isAutoRunSafe, RUN_DEBOUNCE_MS } from './useVqeRunOnChange';

type Params = {
  readonly active: boolean;
  /** The tab's engine — narrows the safety projection to one dialect; omitted = strictest of all. */
  readonly engineId?: DatabaseEngineId;
  /** Re-classifies the live buffer at the view's shown statement (same one `runVisualStatement` runs). */
  readonly resolveVisualRun: () => VisualStatementRun | null;
  /** Runs the Visual view's shown statement, resolved fresh from the live buffer. */
  readonly executeStatement: () => void;
};

export function useVqeVisualRunOnChange({
  active, engineId, resolveVisualRun, executeStatement,
}: Params): void {
  const commitFeedback = useVqeStore((s) => s.commitFeedback);
  const lastHandledRef = useRef(commitFeedback);
  const executeRef = useRef(executeStatement);
  executeRef.current = executeStatement;
  const resolveRef = useRef(resolveVisualRun);
  resolveRef.current = resolveVisualRun;

  useEffect(() => {
    if (!active) {
      // Never replay a commit that landed while disabled.
      lastHandledRef.current = commitFeedback;
      return undefined;
    }
    if (!commitFeedback || commitFeedback === lastHandledRef.current) return undefined;
    lastHandledRef.current = commitFeedback;
    if (commitFeedback.kind !== 'success') return undefined;
    const timer = setTimeout(() => {
      const run = resolveRef.current();
      if (!run || run.kind !== 'select') return;
      const sql = run.sql.trim();
      if (!sql || !isAutoRunSafe(sql, engineId)) return;
      executeRef.current();
    }, RUN_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [active, commitFeedback, engineId]);
}
